import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";
import { Skeleton } from "@/components/ui/skeleton";
import { format } from "date-fns";
import type { VoiceAgentDailyStats } from "@/hooks/useVoiceAgentAnalytics";

interface VoiceAgentUsageChartProps {
  dailyStats: VoiceAgentDailyStats[];
  isLoading: boolean;
}

const VoiceAgentUsageChart = ({ dailyStats, isLoading }: VoiceAgentUsageChartProps) => {
  if (isLoading) {
    return <Skeleton className="h-[350px] w-full" />;
  }

  if (dailyStats.length === 0) {
    return (
      <div className="h-[350px] flex items-center justify-center text-muted-foreground">
        <p>No usage data available yet</p>
      </div>
    );
  }

  const chartData = dailyStats.map((stat) => ({
    date: format(new Date(stat.date), "MMM d"),
    sessions: stat.total_sessions,
    messages: stat.total_messages,
    users: stat.unique_users,
  }));

  return (
    <div className="h-[350px]">
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={chartData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
          <defs>
            <linearGradient id="colorSessions" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="hsl(var(--primary))" stopOpacity={0.3} />
              <stop offset="95%" stopColor="hsl(var(--primary))" stopOpacity={0} />
            </linearGradient>
            <linearGradient id="colorMessages" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="hsl(142, 76%, 36%)" stopOpacity={0.3} />
              <stop offset="95%" stopColor="hsl(142, 76%, 36%)" stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
          <XAxis 
            dataKey="date" 
            className="text-xs"
            tick={{ fill: "hsl(var(--muted-foreground))" }}
          />
          <YAxis 
            className="text-xs"
            tick={{ fill: "hsl(var(--muted-foreground))" }}
          />
          <Tooltip 
            contentStyle={{ 
              backgroundColor: "hsl(var(--background))", 
              border: "1px solid hsl(var(--border))",
              borderRadius: "8px",
            }}
            labelStyle={{ color: "hsl(var(--foreground))" }}
          />
          <Legend />
          <Area
            type="monotone"
            dataKey="sessions"
            name="Sessions"
            stroke="hsl(var(--primary))"
            fillOpacity={1}
            fill="url(#colorSessions)"
          />
          <Area
            type="monotone"
            dataKey="messages"
            name="Messages"
            stroke="hsl(142, 76%, 36%)"
            fillOpacity={1}
            fill="url(#colorMessages)"
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
};

export default VoiceAgentUsageChart;
